import React, { useState, useEffect } from 'react';
import ImageUpload from './ImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const TECH_BACKEND_URL = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/estimates` : 'http://localhost:8002';

// Pull the tech's line-item before/after photos (stored on the NAS via tech-backend,
// keyed by Quotation + line) so they surface here in Project Photos, not just on the
// estimate. Returns them in the same shape the gallery grid renders, flagged source:'job'
// (read-only — they're managed by the tech app, not deletable from the portal).
async function fetchJobPhotos(customerId) {
  try {
    const qres = await fetch(`${BACKEND_URL}/api/customer/${customerId}/quotations`);
    if (!qres.ok) return [];
    const quotations = await qres.json();
    const list = Array.isArray(quotations) ? quotations : (quotations.data || []);
    const perQuote = await Promise.all(
      list.map(async (q) => {
        const qid = q.name || q.id;
        if (!qid) return [];
        try {
          const pres = await fetch(`${TECH_BACKEND_URL}/api/photos/Quotation/${qid}`);
          if (!pres.ok) return [];
          const photos = await pres.json();
          const arr = Array.isArray(photos) ? photos : (photos.data || []);
          return arr.map((p) => ({
            id: `job-${p.id}`,
            source: 'job',
            url: `${TECH_BACKEND_URL}/api/photos/${p.id}/view`,
            name: `${qid}${p.line_idx ? ` · line ${p.line_idx}` : ''}${p.photo_type ? ` · ${p.photo_type}` : ''}`,
            filename: `${qid}${p.line_idx ? `-line${p.line_idx}` : ''}${p.photo_type ? `-${p.photo_type}` : ''}.jpg`,
            created_at: p.created_at || p.uploaded_at || q.custom_scheduled_date || null,
          }));
        } catch { return []; }
      })
    );
    return perQuote.flat();
  } catch {
    return [];
  }
}

export default function ImagesGallery({ customerId }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/customer/${customerId}/images`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch images');
      }

      const data = await response.json();
      const uploaded = data.data || [];
      const jobPhotos = await fetchJobPhotos(customerId);
      // Uploaded customer photos first, then the tech's job photos.
      setImages([...uploaded, ...jobPhotos]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerId) {
      fetchImages();
    }
  }, [customerId]);

  const handleUploadSuccess = () => {
    fetchImages();
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/customer/${customerId}/images/${imageId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) throw new Error('Delete failed');
      fetchImages();
    } catch (err) {
      alert('Error deleting image: ' + err.message);
    }
  };

  if (loading && images.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        <p>Loading images...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ color: '#1b7abf', marginBottom: '10px' }}>📷 Project Photos</h2>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        Upload photos from your service appointments or projects. You can use these images in letters or reports.
      </p>

      <ImageUpload customerId={customerId} onUploadSuccess={handleUploadSuccess} />

      {error && (
        <div style={{
          padding: '12px',
          backgroundColor: '#fee2e2',
          border: '1px solid #fca5a5',
          borderRadius: '6px',
          color: '#dc2626',
          marginBottom: '20px'
        }}>
          ❌ {error}
        </div>
      )}

      {images.length === 0 ? (
        <div style={{
          backgroundColor: '#f0fdf4',
          border: '1px solid #dcfce7',
          borderRadius: '8px',
          padding: '30px',
          textAlign: 'center',
          color: '#166534'
        }}>
          <p style={{ fontSize: '16px', margin: 0 }}>✓ No photos uploaded yet</p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '16px',
          marginTop: '20px'
        }}>
          {images.map((image) => (
            <div
              key={image.id || image.filename}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                backgroundColor: '#fff'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
              }}
              onClick={() => setSelectedImage(image)}
            >
              <div style={{
                width: '100%',
                height: '180px',
                overflow: 'hidden',
                backgroundColor: '#f9fafb'
              }}>
                <img
                  src={image.url || `${BACKEND_URL}/api/images/${image.filename}`}
                  alt={image.filename}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
              <div style={{
                padding: '12px',
                borderTop: '1px solid #e5e7eb'
              }}>
                <p style={{
                  margin: '0 0 4px 0',
                  fontSize: '12px',
                  color: '#666',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {image.filename || image.name}
                </p>
                <p style={{
                  margin: '0 0 8px 0',
                  fontSize: '11px',
                  color: '#999'
                }}>
                  {image.created_at ? new Date(image.created_at).toLocaleDateString() : ''}
                </p>
                {image.source === 'job' ? (
                  <div
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      backgroundColor: '#e0f2fe',
                      border: '1px solid #7dd3fc',
                      borderRadius: '4px',
                      color: '#075985',
                      fontSize: '11px',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}
                  >
                    🛠️ Job photo
                  </div>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteImage(image.id);
                    }}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      backgroundColor: '#fee2e2',
                      border: '1px solid #fca5a5',
                      borderRadius: '4px',
                      color: '#dc2626',
                      fontSize: '11px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    🗑️ Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedImage && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px'
          }}
          onClick={() => setSelectedImage(null)}
        >
          <div
            style={{
              position: 'relative',
              backgroundColor: '#fff',
              borderRadius: '12px',
              overflow: 'hidden',
              maxWidth: '90vw',
              maxHeight: '90vh',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedImage(null)}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                zIndex: 10,
                background: 'rgba(0,0,0,0.5)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>

            <img
              src={selectedImage.url || `${BACKEND_URL}/api/images/${selectedImage.filename}`}
              alt={selectedImage.filename}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '85vh',
                objectFit: 'contain'
              }}
            />

            <div style={{
              padding: '16px',
              backgroundColor: '#f9fafb',
              borderTop: '1px solid #e5e7eb'
            }}>
              <p style={{ margin: '0 0 8px 0', color: '#333', fontWeight: 'bold' }}>
                {selectedImage.filename}
              </p>
              <div style={{
                display: 'flex',
                gap: '10px'
              }}>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(
                      selectedImage.url || `${BACKEND_URL}/api/images/${selectedImage.filename}`
                    );
                    alert('Image URL copied to clipboard!');
                  }}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    backgroundColor: '#1b7abf',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  📋 Copy URL
                </button>
                <a
                  href={selectedImage.url || `${BACKEND_URL}/api/images/${selectedImage.filename}`}
                  download
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    backgroundColor: '#ea580c',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    textDecoration: 'none',
                    textAlign: 'center'
                  }}
                >
                  📥 Download
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
